/**
 * =============================================================================
 * CommissionDetailSheet — Sheet lateral de detalhes de uma Comissão (Indicação)
 *
 * Padronizado 1:1 com `PayoutDetailSheet` (módulo Indicação / Pagamentos):
 *   - Sheet à direita, largura `w-[480px]` em desktop e fullscreen no mobile.
 *   - Header com chip de ícone + nome do afiliado + UUID curto + badge status.
 *   - Barra de ações contextuais (aprovar / pagar agora / cancelar) — só
 *     renderizada se o usuário possui `canEdit('referral_commissions')`.
 *   - Blocos: Informações gerais, Origem do crédito (negócio/cliente/lead),
 *     Afiliado, Pagamentos vinculados (clicáveis) e Timeline cronológica.
 *   - Realtime via `useReferralCommissionDetails`.
 *
 * Segurança/UX:
 *   - UUIDs renderizados em 8 chars uppercase. A própria RPC já entrega o
 *     payload mascarado via `format_jsonb_uuids` no servidor.
 *   - Ações dependem de permissão de edição (não basta visualizar).
 *
 * @version 1.0.0
 * RELEASE:
 *   2026-04-30 v1.0.0 - Criação espelhando PayoutDetailSheet.
 * =============================================================================
 */
import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  HandCoins,
  CheckCircle2,
  XCircle,
  CircleDollarSign,
  Clock,
  Loader2,
  Ban,
  AlertCircle,
  History,
  User as UserIcon,
  Briefcase,
  ExternalLink,
  StickyNote,
  Mail,
  KeyRound,
  Info,
  Receipt,
  Wallet,
  Tag,
  Target,
  Megaphone,
  Link2,
  Unlink,
  Activity,
  Building2,
  Compass,
  FolderTree,
} from 'lucide-react';
import { DealStatusBadge } from '@/components/crm/deals/DealStatusBadge';
import { CommissionStatusBadge } from '@/components/referral/CommissionStatusBadge';
import { DealPhaseProgress } from '@/components/crm/deals/DealPhaseProgress';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import {
  useReferralCommissionDetails,
  type CommissionDetailsPayout,
  type CommissionTimelineEvent,
  type CommissionTimelineAction,
} from '@/hooks/useReferralCommissionDetails';
import type { CommissionStatus } from '@/hooks/useReferralCommissionsPaginated';
import { usePermissions } from '@/hooks/usePermissions';

// ============================================================================
// Tipagem de ações (espelha as do CommissionsTableView)
// ============================================================================

export type CommissionDetailAction = 'approve' | 'mark_paid' | 'cancel';

interface CommissionDetailSheetProps {
  /** ID da comissão — `null` mantém o Sheet fechado. */
  commissionId: string | null;
  /** Callback de fechamento do Sheet. */
  onClose: () => void;
  /** Aciona um item de menu (mapeado para handlers da página). */
  onAction: (commissionId: string, action: CommissionDetailAction) => void;
  /** Abre o modal premium do negócio (CRM). */
  onOpenDeal?: (dealId: string) => void;
  /** Abre o modal premium do cliente (CRM). */
  onOpenCustomer?: (customerId: string) => void;
  /** Abre o sheet de detalhes do payout vinculado. */
  onOpenPayout?: (payoutId: string) => void;
}

// ============================================================================
// CONSTANTES DE STATUS
// ============================================================================

const STATUS_CONFIG: Record<CommissionStatus, { label: string; color: string; dot: string }> = {
  pending:   { label: 'Pendente',  color: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/60', dot: 'bg-amber-500' },
  approved:  { label: 'Aprovada',  color: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/60',       dot: 'bg-blue-500' },
  paid:      { label: 'Paga',      color: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60', dot: 'bg-emerald-500' },
  cancelled: { label: 'Cancelada', color: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900/60',       dot: 'bg-rose-500' },
  reversed:  { label: 'Revertida', color: 'bg-muted text-muted-foreground border-border',                                                                  dot: 'bg-muted-foreground' },
};

const REWARD_TYPE_LABEL: Record<string, string> = {
  fixed: 'Valor fixo',
  percentage: 'Percentual',
  points: 'Pontos',
  recurring: 'Recorrente',
  tiered: 'Em níveis',
};

const PAYOUT_STATUS_CONFIG: Record<string, { label: string; color: string; dot: string }> = {
  pending:    { label: 'Pendente',    color: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300', dot: 'bg-amber-500' },
  processing: { label: 'Processando', color: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300',      dot: 'bg-blue-500' },
  paid:       { label: 'Pago',        color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300', dot: 'bg-emerald-500' },
  failed:     { label: 'Falhou',      color: 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300',      dot: 'bg-rose-500' },
  cancelled:  { label: 'Cancelado',   color: 'bg-muted text-muted-foreground',                                          dot: 'bg-muted-foreground' },
};

// ============================================================================
// HELPERS
// ============================================================================

/** Formata valor monetário em BRL. */
function formatBRL(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

/** Formata CPF/CNPJ — devolve string mascarada ou null. */
function formatCpfCnpj(cpf?: string | null, cnpj?: string | null): string | null {
  const raw = (cnpj || cpf || '').replace(/\D/g, '');
  if (!raw) return null;
  if (raw.length === 11) return raw.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (raw.length === 14) return raw.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return cnpj || cpf || null;
}

/** Formata data longa (`dd/MM/yyyy 'às' HH:mm`). */
function formatLong(date: string | null | undefined): string | null {
  if (!date) return null;
  return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

/**
 * Trunca um id (vindo já mascarado pelo backend ou completo) para 8 chars
 * uppercase. Idempotente — funciona em ambos os formatos.
 */
function shortId(id: string | null | undefined): string {
  if (!id) return '';
  return id.substring(0, 8).toUpperCase();
}

// ============================================================================
// SUBCOMPONENTES VISUAIS (idênticos ao PayoutDetailSheet)
// ============================================================================

function SectionLabel({
  icon: Icon,
  children,
  right,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {children}
      </div>
      {right}
    </div>
  );
}

function InfoRow({
  label,
  value,
  icon: Icon,
  className,
}: {
  label: string;
  value: React.ReactNode;
  /** Ícone opcional exibido à esquerda do label (padrão modal Negócio). */
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex justify-between gap-3 px-3 py-2 text-sm rounded-md',
        className,
      )}
    >
      <span className="text-xs text-muted-foreground shrink-0 self-center inline-flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground/70" />}
        {label}
      </span>
      <span className="text-right text-sm font-medium tabular-nums break-words self-center">
        {value}
      </span>
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Action Bar contextual (gated por permissão)
// ----------------------------------------------------------------------------
// Só aparece se `canEdit` for true. Caso contrário, o usuário pode VER
// os detalhes mas não executar ações.
// ============================================================================

function CommissionActionBar({
  status,
  commissionId,
  hasLinkedPayout,
  onAction,
}: {
  status: CommissionStatus;
  commissionId: string;
  hasLinkedPayout: boolean;
  onAction: CommissionDetailSheetProps['onAction'];
}) {
  // Mesmas regras do CommissionsTableView (linhas 480-503).
  const canApprove = status === 'pending';
  // Pagar agora só se está aprovada e ainda não tem payout vinculado.
  const canMarkPaid = status === 'approved' && !hasLinkedPayout;
  const canCancel = status === 'pending' || status === 'approved';

  if (!canApprove && !canMarkPaid && !canCancel) return null;

  const baseBtn = 'h-8 px-3 text-xs font-medium gap-1.5 rounded-md';

  // PRIMÁRIA por status:
  //  - pending  → Aprovar (azul)
  //  - approved → Pagar agora (verde)
  const primaryAction: 'approve' | 'mark_paid' | null = canApprove
    ? 'approve'
    : canMarkPaid
      ? 'mark_paid'
      : null;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {canApprove && primaryAction === 'approve' && (
          <Button
            onClick={() => onAction(commissionId, 'approve')}
            className={cn(
              baseBtn,
              'col-span-2 bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
            )}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Aprovar comissão
          </Button>
        )}

        {canMarkPaid && primaryAction === 'mark_paid' && (
          <Button
            onClick={() => onAction(commissionId, 'mark_paid')}
            className={cn(
              baseBtn,
              'col-span-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm',
            )}
          >
            <CircleDollarSign className="h-3.5 w-3.5" />
            Pagar agora
          </Button>
        )}
      </div>

      {/* Cancelar — link discreto, alinhado à direita. */}
      {canCancel && (
        <div className="flex justify-end pt-0.5">
          <Button
            variant="ghost"
            onClick={() => onAction(commissionId, 'cancel')}
            className="h-7 px-2 text-xs font-normal gap-1 text-muted-foreground hover:text-destructive hover:bg-transparent"
          >
            <Ban className="h-3 w-3" />
            Cancelar comissão
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Card de pagamento vinculado
// ============================================================================

function PayoutCardRow({
  payout,
  onOpenPayout,
}: {
  payout: CommissionDetailsPayout;
  onOpenPayout?: (payoutId: string) => void;
}) {
  const statusInfo =
    PAYOUT_STATUS_CONFIG[payout.payout_status] ?? PAYOUT_STATUS_CONFIG.pending;
  const clickable = !!onOpenPayout && !!payout.payout_id;

  return (
    <div
      className={cn(
        'group relative flex gap-3 rounded-xl border border-border/60 bg-card p-3',
        'transition-all duration-200 hover:border-border hover:shadow-sm',
        // Vínculo "released" (desfeito) → borda lateral mais discreta
        payout.is_active
          ? 'border-l-[3px] border-l-primary/40 hover:border-l-primary'
          : 'border-l-[3px] border-l-muted-foreground/30 opacity-80',
      )}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Wallet className="h-5 w-5" />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Pagamento
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (clickable) onOpenPayout!(payout.payout_id);
            }}
            disabled={!clickable}
            className={cn(
              'inline-flex items-center gap-1 font-mono text-xs uppercase min-w-0',
              clickable
                ? 'text-primary hover:underline cursor-pointer'
                : 'text-muted-foreground cursor-default',
            )}
            title={clickable ? 'Abrir pagamento' : undefined}
          >
            <span className="truncate">#{shortId(payout.payout_id)}</span>
            {clickable && <ExternalLink className="h-2.5 w-2.5 opacity-60 shrink-0" />}
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            variant="outline"
            className={cn('gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium', statusInfo.color)}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', statusInfo.dot)} />
            {statusInfo.label}
          </Badge>
          {!payout.is_active && (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              <Unlink className="h-2.5 w-2.5" />
              Desvinculado
            </span>
          )}
        </div>

        {/* Datas relevantes */}
        <div className="text-[11px] text-muted-foreground tabular-nums leading-tight space-y-0.5">
          {payout.linked_at && (
            <div>
              Vinculado em <span className="text-foreground/80">{formatLong(payout.linked_at)}</span>
            </div>
          )}
          {payout.paid_at && (
            <div>
              Pago em <span className="text-foreground/80">{formatLong(payout.paid_at)}</span>
            </div>
          )}
          {payout.released_at && (
            <div>
              Desvinculado em <span className="text-foreground/80">{formatLong(payout.released_at)}</span>
            </div>
          )}
        </div>

        {payout.released_reason && (
          <p className="text-[11px] text-muted-foreground italic">
            Motivo: {payout.released_reason}
          </p>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end justify-between gap-1.5">
        <span className="text-base font-bold tabular-nums text-foreground leading-none">
          {formatBRL(payout.link_reward_value ?? payout.payout_amount)}
        </span>
        {payout.payout_amount && payout.link_reward_value &&
          Number(payout.payout_amount) !== Number(payout.link_reward_value) && (
            <span className="text-[10px] text-muted-foreground/80 tabular-nums">
              Total pgto: {formatBRL(payout.payout_amount)}
            </span>
          )}
      </div>
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Timeline cronológica
// ============================================================================

const ACTION_VISUAL: Record<
  CommissionTimelineAction,
  {
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    iconBg: string;
    label: string;
  }
> = {
  created:         { icon: Clock,        color: 'text-amber-600 dark:text-amber-400',     iconBg: 'bg-amber-100 dark:bg-amber-950/40',     label: 'Comissão gerada' },
  approved:        { icon: CheckCircle2, color: 'text-blue-600 dark:text-blue-400',       iconBg: 'bg-blue-100 dark:bg-blue-950/40',       label: 'Comissão aprovada' },
  payout_linked:   { icon: Link2,        color: 'text-indigo-600 dark:text-indigo-400',   iconBg: 'bg-indigo-100 dark:bg-indigo-950/40',   label: 'Vinculada a pagamento' },
  payout_released: { icon: Unlink,       color: 'text-muted-foreground',                  iconBg: 'bg-muted',                              label: 'Desvinculada do pagamento' },
  paid:            { icon: CircleDollarSign, color: 'text-emerald-600 dark:text-emerald-400', iconBg: 'bg-emerald-100 dark:bg-emerald-950/40', label: 'Pagamento confirmado' },
  cancelled:       { icon: Ban,          color: 'text-rose-600 dark:text-rose-400',       iconBg: 'bg-rose-100 dark:bg-rose-950/40',       label: 'Comissão cancelada' },
};

function CommissionTimeline({
  events,
  onOpenPayout,
}: {
  events: CommissionTimelineEvent[];
  onOpenPayout?: (payoutId: string) => void;
}) {
  // Cronológico: mais antigo → mais novo.
  const ordered = React.useMemo(
    () =>
      [...events].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      ),
    [events],
  );

  if (ordered.length === 0) {
    return <p className="text-xs text-muted-foreground">Nenhum evento registrado.</p>;
  }

  return (
    <ol className="relative space-y-4 pl-1">
      {ordered.map((ev, idx) => {
        const visual = ACTION_VISUAL[ev.action] ?? ACTION_VISUAL.created;
        const Icon = visual.icon;
        const isLast = idx === ordered.length - 1;

        const performerLabel =
          ev.performer_name?.trim() || ev.performer_email?.trim() || 'Sistema';

        const referenceClickable =
          ev.reference_type === 'payout' && !!ev.reference_id && !!onOpenPayout;

        return (
          <li key={ev.id} className="relative flex gap-3">
            {!isLast && (
              <span
                aria-hidden
                className="absolute left-[13px] top-7 bottom-[-12px] w-px bg-border/60"
              />
            )}
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-4 ring-background',
                visual.iconBg,
              )}
            >
              <Icon className={cn('h-3.5 w-3.5', visual.color)} />
            </span>

            <div className="flex-1 min-w-0 pt-0.5">
              <p className="text-sm font-semibold leading-tight">{visual.label}</p>

              {/* Quem executou */}
              {(ev.performer_name || ev.performer_email) && (
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed flex items-center gap-1 flex-wrap">
                  <UserIcon className="h-3 w-3 shrink-0" />
                  <span>por</span>
                  <span className="font-medium text-foreground">{performerLabel}</span>
                  {ev.performer_email && ev.performer_name && (
                    <span className="text-muted-foreground/70">({ev.performer_email})</span>
                  )}
                </p>
              )}

              {/* Referência (payout) */}
              {ev.reference_id && ev.reference_type === 'payout' && (
                <p className="text-xs mt-1">
                  Pagamento:{' '}
                  {referenceClickable ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenPayout!(ev.reference_id!);
                      }}
                      className="font-mono text-primary hover:underline"
                    >
                      #{shortId(ev.reference_id)}
                    </button>
                  ) : (
                    <span className="font-mono text-muted-foreground">
                      #{shortId(ev.reference_id)}
                    </span>
                  )}
                </p>
              )}

              {(ev.reason || ev.notes) && (
                <p className="text-xs text-muted-foreground mt-1 break-words leading-relaxed">
                  {ev.reason && (
                    <>
                      <span className="font-medium text-foreground/80">Motivo:</span> {ev.reason}
                    </>
                  )}
                  {ev.reason && ev.notes && <br />}
                  {ev.notes}
                </p>
              )}

              <p className="text-[11px] text-muted-foreground/70 mt-1 tabular-nums">
                {formatLong(ev.created_at)}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL — CommissionDetailSheet
// ============================================================================

export function CommissionDetailSheet({
  commissionId,
  onClose,
  onAction,
  onOpenDeal,
  onOpenCustomer,
  onOpenPayout,
}: CommissionDetailSheetProps) {
  const open = !!commissionId;
  const { data, isLoading, error } = useReferralCommissionDetails(commissionId, open);

  // Permissão: só renderizamos a action bar se o usuário pode editar.
  // Visualizar continua liberado conforme a regra do menu.
  const { canEdit } = usePermissions();
  const canEditCommissions = canEdit('referral_commissions');

  const commission = data?.commission;
  const affiliate = data?.affiliate;
  const payouts = data?.payouts ?? [];
  const timeline = data?.timeline ?? [];

  const statusInfo = commission ? STATUS_CONFIG[commission.status] ?? STATUS_CONFIG.pending : null;

  // Pagamento vinculado ATIVO controla se "Pagar agora" deve aparecer.
  const hasActivePayout = payouts.some((p) => p.is_active);

  // Fallback CPF/CNPJ formatado
  const customerDoc = commission
    ? formatCpfCnpj(commission.customer_cpf, commission.customer_cnpj)
    : null;

  const valorPrincipal =
    commission?.reward_type === 'points'
      ? `${commission?.points_earned ?? 0} pts`
      : formatBRL(commission?.reward_value);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        className="!w-full sm:!w-[480px] sm:!max-w-[480px] p-0 overflow-x-hidden bg-background"
        aria-describedby={undefined}
      >
        {/* Acessibilidade: Radix exige um SheetTitle (mesmo oculto) já no
            primeiro render para evitar o warning "DialogContent requires a
            DialogTitle". Em loading/erro mantemos um título acessível porém
            visualmente discreto. */}
        {isLoading || (!commission && !error) ? (
          <div className="p-6 space-y-4">
            <SheetHeader className="text-left">
              <SheetTitle className="text-base font-semibold">
                Carregando detalhes da comissão…
              </SheetTitle>
            </SheetHeader>
            <Skeleton className="h-9 w-9 rounded-xl" />
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : error || !commission ? (
          // Estado de erro explícito — evita "tela em branco" quando a RPC
          // falha (ex: 42P01, 42501). Antes ficava preso no skeleton.
          <div className="p-6 space-y-4">
            <SheetHeader className="text-left">
              <SheetTitle className="flex items-center gap-2 text-base font-semibold">
                <AlertCircle className="h-5 w-5 text-destructive" />
                Não foi possível carregar a comissão
              </SheetTitle>
            </SheetHeader>
            <p className="text-sm text-muted-foreground">
              {(error as Error | null)?.message
                ?? 'Ocorreu um erro inesperado ao buscar os detalhes desta comissão. Tente novamente em instantes.'}
            </p>
            <Button variant="outline" size="sm" onClick={onClose}>
              Fechar
            </Button>
          </div>
        ) : (
          <ScrollArea className="h-full">
            {/* ============== Header com gradient sutil ============== */}
            <div className="relative bg-gradient-to-b from-muted/50 via-muted/15 to-transparent px-5 sm:px-6 pt-6 pb-5">
              <SheetHeader className="space-y-3 text-left">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15">
                    <HandCoins className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <SheetTitle className="text-base font-semibold leading-tight truncate">
                      {affiliate?.full_name ?? 'Comissão'}
                    </SheetTitle>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[11px] text-muted-foreground font-mono uppercase">
                        #{shortId(commission.id)}
                      </span>
                      {statusInfo && (
                        <>
                          <span className="text-muted-foreground/50">·</span>
                          {/* Status no formato unificado: bolinha colorida + texto */}
                          <CommissionStatusBadge status={commission.status} />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </SheetHeader>
            </div>

            {/* ============== Corpo ============== */}
            <div className="px-5 sm:px-6 pb-8 space-y-7">
              {/* Action Bar (gated por permissão) */}
              {canEditCommissions && (
                <CommissionActionBar
                  status={commission.status}
                  commissionId={commission.id}
                  hasLinkedPayout={hasActivePayout}
                  onAction={onAction}
                />
              )}

              {/* ============== Bloco: Informações gerais ============== */}
              <section className="space-y-3">
                <SectionLabel icon={Info}>Informações gerais</SectionLabel>
                <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                  <InfoRow
                    label="Valor"
                    value={
                      <span className="text-base font-semibold tabular-nums text-foreground">
                        {valorPrincipal}
                      </span>
                    }
                  />
                  <InfoRow
                    label="Tipo de recompensa"
                    value={REWARD_TYPE_LABEL[commission.reward_type] ?? commission.reward_type}
                    className="bg-muted/20"
                  />
                  {commission.base_value != null && (
                    <InfoRow
                      label="Base de cálculo"
                      value={formatBRL(commission.base_value)}
                    />
                  )}
                  {commission.currency && (
                    <InfoRow
                      label="Moeda"
                      value={commission.currency}
                      className="bg-muted/20"
                    />
                  )}
                  <InfoRow
                    label="Criada em"
                    value={formatLong(commission.created_at) ?? '—'}
                  />
                  {commission.approved_at && (
                    <InfoRow
                      label="Aprovada em"
                      value={
                        <div className="flex flex-col items-end gap-0.5">
                          <span>{formatLong(commission.approved_at)}</span>
                          {commission.approved_by_name && (
                            <span className="text-[11px] text-muted-foreground font-normal">
                              por {commission.approved_by_name}
                            </span>
                          )}
                        </div>
                      }
                      className="bg-muted/20"
                    />
                  )}
                  {commission.paid_at && (
                    <InfoRow
                      label="Paga em"
                      value={formatLong(commission.paid_at)}
                    />
                  )}
                  {commission.cancelled_at && (
                    <InfoRow
                      label="Cancelada em"
                      value={formatLong(commission.cancelled_at)}
                      className="bg-muted/20"
                    />
                  )}
                </div>

                {/* Motivo de cancelamento em destaque */}
                {commission.cancelled_reason && commission.status === 'cancelled' && (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 flex gap-2.5">
                    <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <div className="text-xs leading-relaxed">
                      <p className="font-semibold text-destructive">Motivo do cancelamento</p>
                      <p className="text-muted-foreground mt-1">
                        {commission.cancelled_reason}
                      </p>
                    </div>
                  </div>
                )}

                {/* Observações */}
                {commission.notes && (
                  <div className="rounded-xl border border-border/60 bg-card p-3 flex gap-2.5">
                    <StickyNote className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <p className="text-xs whitespace-pre-wrap break-words leading-relaxed">
                      {commission.notes}
                    </p>
                  </div>
                )}
              </section>

              {/* ============== Bloco: Origem do crédito (Programa/Campanha) ============== */}
              {(commission.program_name || commission.campaign_name) && (
                <section className="space-y-3">
                  <SectionLabel icon={Target}>Origem do crédito</SectionLabel>
                  <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                    {commission.program_name && (
                      <InfoRow
                        label="Programa"
                        value={
                          <span className="inline-flex items-center gap-1.5">
                            <Megaphone className="h-3 w-3 text-muted-foreground" />
                            {commission.program_name}
                          </span>
                        }
                      />
                    )}
                    {commission.campaign_name && (
                      <InfoRow
                        label="Campanha"
                        value={
                          <span className="inline-flex items-center gap-1.5">
                            <Tag className="h-3 w-3 text-muted-foreground" />
                            {commission.campaign_name}
                          </span>
                        }
                        className={cn(commission.program_name && 'bg-muted/20')}
                      />
                    )}
                  </div>
                </section>
              )}

              {/* ============== Bloco: Afiliado ==============
                  v1.1.0: Movido para vir ANTES de Cliente/Integração CRM,
                  espelhando a ordem do `ReferralReferralDetailSheet`. Adiciona
                  avatar à esquerda (SmartAvatar com fallback 3D).
              */}
              <section className="space-y-3">
                <SectionLabel icon={UserIcon}>Afiliado</SectionLabel>
                <div className="rounded-xl border border-border/60 bg-card p-3 flex items-start gap-3">
                  {affiliate ? (
                    <>
                      <SmartAvatar
                        src={affiliate.avatar_url}
                        name={affiliate.full_name || affiliate.email || '?'}
                        size="md"
                        use3DFallback
                      />
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold truncate">
                            {affiliate.full_name ?? '—'}
                          </p>
                          {affiliate.id && (
                            <p className="text-[11px] text-muted-foreground/70 flex items-center gap-1.5 shrink-0">
                              <KeyRound className="h-3 w-3" />
                              <span>ID:</span>
                              <span className="font-mono uppercase">{shortId(affiliate.id)}</span>
                            </p>
                          )}
                        </div>
                        {affiliate.email && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5" />
                            {affiliate.email}
                          </p>
                        )}
                        {affiliate.pix_key && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1.5 break-all">
                            <Receipt className="h-3.5 w-3.5 shrink-0" />
                            PIX{affiliate.pix_key_type ? ` (${affiliate.pix_key_type.toUpperCase()})` : ''}:{' '}
                            <span className="font-mono">{affiliate.pix_key}</span>
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm italic text-muted-foreground flex items-center gap-1.5">
                      <AlertCircle className="h-4 w-4" />
                      Afiliado removido
                    </p>
                  )}
                </div>
              </section>


              {/* ============== Bloco: Cliente (cliente vinculado ou lead indicado) ============== */}
              {(commission.customer_id || commission.referral_lead_name) && (
                <section className="space-y-3">
                  <SectionLabel icon={UserIcon}>Cliente</SectionLabel>
                  <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                    {commission.customer_id ? (
                      <>
                        <InfoRow
                          label="Cliente"
                          value={
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onOpenCustomer && commission.customer_id) {
                                  onOpenCustomer(commission.customer_id);
                                }
                              }}
                              disabled={!onOpenCustomer}
                              className={cn(
                                'inline-flex items-center gap-1 max-w-full text-right',
                                onOpenCustomer
                                  ? 'text-primary hover:underline cursor-pointer'
                                  : 'text-foreground cursor-default',
                              )}
                            >
                              <span className="truncate">{commission.customer_name ?? '—'}</span>
                              {onOpenCustomer && (
                                <ExternalLink className="h-2.5 w-2.5 opacity-60 shrink-0" />
                              )}
                            </button>
                          }
                        />
                        {customerDoc && (
                          <InfoRow
                            label={commission.customer_cnpj ? 'CNPJ' : 'CPF'}
                            value={<span className="font-mono text-xs">{customerDoc}</span>}
                            className="bg-muted/20"
                          />
                        )}
                      </>
                    ) : commission.referral_lead_name ? (
                      <>
                        <InfoRow
                          label="Lead indicado"
                          value={
                            <span className="inline-flex items-center gap-1.5">
                              {commission.referral_lead_name}
                              <span className="rounded-md bg-warning/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning">
                                Lead
                              </span>
                            </span>
                          }
                        />
                        {commission.referral_lead_email && (
                          <InfoRow
                            label="E-mail do lead"
                            value={
                              <span className="text-xs break-all">
                                {commission.referral_lead_email}
                              </span>
                            }
                            className="bg-muted/20"
                          />
                        )}
                        {commission.referral_lead_phone && (
                          <InfoRow
                            label="Telefone do lead"
                            value={<span className="font-mono text-xs">{commission.referral_lead_phone}</span>}
                          />
                        )}
                      </>
                    ) : null}
                  </div>
                </section>
              )}

              {/* ============== Bloco: Integração CRM ==============
                  v1.1.0: Padronizado com `ReferralReferralDetailSheet`:
                   - Cada label com ícone à esquerda;
                   - Status do negócio via DealStatusBadge;
                   - Fase atual com DealPhaseProgress (barra colorida);
                   - Vendedor com SmartAvatar (fallback 3D).
              */}
              {(commission.deal_id ||
                commission.deal_title ||
                commission.deal_value != null ||
                commission.deal_phase_name ||
                commission.seller_name) && (
                <section className="space-y-3">
                  <SectionLabel icon={Briefcase}>Integração CRM</SectionLabel>
                  <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                    {/* Status do negócio */}
                    {commission.deal_status && (
                      <InfoRow
                        icon={Activity}
                        label="Status do negócio"
                        value={<DealStatusBadge status={commission.deal_status} />}
                      />
                    )}

                    {/* Negócio (#ID clicável) */}
                    {commission.deal_id && (
                      <InfoRow
                        icon={Briefcase}
                        label="Negócio"
                        value={
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onOpenDeal && commission.deal_id) onOpenDeal(commission.deal_id);
                            }}
                            disabled={!onOpenDeal}
                            className={cn(
                              'inline-flex items-center gap-1 font-mono text-xs uppercase max-w-full',
                              onOpenDeal
                                ? 'text-primary hover:underline cursor-pointer'
                                : 'text-muted-foreground cursor-default',
                            )}
                            title={commission.deal_title || undefined}
                          >
                            <span className="truncate">#{shortId(commission.deal_id)}</span>
                            {onOpenDeal && <ExternalLink className="h-2.5 w-2.5 opacity-60 shrink-0" />}
                          </button>
                        }
                        className={cn(commission.deal_status && 'bg-muted/20')}
                      />
                    )}

                    {commission.deal_title && (
                      <InfoRow
                        icon={Tag}
                        label="Título do negócio"
                        value={
                          <span className="text-sm text-foreground break-words">
                            {commission.deal_title}
                          </span>
                        }
                      />
                    )}
                    {commission.deal_value != null && (
                      <InfoRow
                        icon={CircleDollarSign}
                        label="Valor do negócio"
                        value={formatBRL(commission.deal_value)}
                        className="bg-muted/20"
                      />
                    )}

                    {/* Workspace */}
                    {commission.deal_workspace_name && (
                      <InfoRow
                        icon={Building2}
                        label="Workspace"
                        value={commission.deal_workspace_name}
                      />
                    )}

                    {/* Origem do negócio */}
                    {commission.deal_source && (
                      <InfoRow
                        icon={Compass}
                        label="Origem do negócio"
                        value={commission.deal_source}
                        className={cn(commission.deal_workspace_name && 'bg-muted/20')}
                      />
                    )}

                    {/* Categoria do workspace */}
                    {commission.deal_category_name && (
                      <InfoRow
                        icon={FolderTree}
                        label="Categoria"
                        value={commission.deal_category_name}
                      />
                    )}

                    {/* Fase atual com barra evolutiva */}
                    {commission.deal_phase_name && (
                      <InfoRow
                        icon={Target}
                        label="Fase atual"
                        value={
                          <div className="min-w-[140px]">
                            <DealPhaseProgress
                              phaseName={commission.deal_phase_name}
                              phaseColor={commission.deal_phase_color}
                              phaseSortOrder={commission.deal_phase_sort_order ?? 1}
                              totalPhases={commission.deal_pipeline_total_phases ?? 1}
                            />
                          </div>
                        }
                        className="bg-muted/20"
                      />
                    )}

                    {/* Vendedor com avatar */}
                    {commission.seller_name && (
                      <InfoRow
                        icon={UserIcon}
                        label="Vendedor"
                        value={
                          <span className="inline-flex items-center gap-2">
                            <SmartAvatar
                              src={commission.seller_avatar_url}
                              name={commission.seller_name}
                              size="xs"
                              use3DFallback
                            />
                            <span className="truncate max-w-[160px]">{commission.seller_name}</span>
                          </span>
                        }
                      />
                    )}
                  </div>
                </section>
              )}
              {/* ============== Bloco: Pagamentos vinculados ============== */}
              <section className="space-y-3">
                <SectionLabel
                  icon={Wallet}
                  right={
                    payouts.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold"
                      >
                        {payouts.length}{' '}
                        {payouts.length === 1 ? 'pagamento' : 'pagamentos'}
                      </Badge>
                    )
                  }
                >
                  Pagamentos vinculados
                </SectionLabel>

                {payouts.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border/60 p-8 text-center">
                    <Wallet className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
                    <p className="text-xs text-muted-foreground">
                      Esta comissão ainda não foi vinculada a nenhum pagamento.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-border/60 bg-gradient-to-b from-card to-muted/10 overflow-hidden">
                    <div className="max-h-[320px] overflow-y-auto scrollbar-thin p-2 space-y-2">
                      {payouts.map((p) => (
                        <PayoutCardRow
                          key={p.link_id}
                          payout={p}
                          onOpenPayout={onOpenPayout}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </section>

              {/* ============== Bloco: Timeline ============== */}
              <section className="space-y-4">
                <SectionLabel icon={History}>Histórico</SectionLabel>
                <CommissionTimeline events={timeline} onOpenPayout={onOpenPayout} />
              </section>
            </div>
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
}
